<!-- EditPaymentForm.vue -->
<script setup>
import { ref, reactive, watch } from "vue";
import { usePaymentStore } from "@/stores/usePaymentStore";

const props = defineProps({
  payment: {
    type: Object,
    required: true,
  },
});
import { useDraggableModal } from "@/composables/useDraggableModal";

const emit = defineEmits(["close", "refresh"]);
const store = usePaymentStore();

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

const isLoading = ref(false);
const errorMessage = ref("");

// ── PATCH: conversão manual UTC <-> UTC-3 para createdAt ─────────
// Banco/API trabalham em UTC.
// Front exibe UTC-3.
// Ex.: banco 2026-05-12T03:00:00 => front 2026-05-12T00:00.
const UTC_MINUS_3_OFFSET_MINUTES = -180;

function pad2(value) {
  return String(value).padStart(2, "0");
}

function parseBackendUtcDate(value) {
  if (!value) return null;

  const raw = String(value).trim();

  // Aceita "2026-05-12T03:40:19", "2026-05-12T03:40:19Z",
  // "2026-05-12 03:40:19"
  const match = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?/
  );

  if (match) {
    const [, year, month, day, hour, minute, second = "0"] = match;

    return new Date(
      Date.UTC(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second)
      )
    );
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toDatetimeLocalUtcMinus3(value) {
  const utcDate = parseBackendUtcDate(value);
  if (!utcDate) return "";

  const shifted = new Date(
    utcDate.getTime() + UTC_MINUS_3_OFFSET_MINUTES * 60 * 1000
  );

  return [
    shifted.getUTCFullYear(),
    "-",
    pad2(shifted.getUTCMonth() + 1),
    "-",
    pad2(shifted.getUTCDate()),
    "T",
    pad2(shifted.getUTCHours()),
    ":",
    pad2(shifted.getUTCMinutes()),
  ].join("");
}

function fromDatetimeLocalUtcMinus3ToUtc(value) {
  if (!value) return null;

  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);

  if (!match) return null;

  const [, year, month, day, hour, minute] = match;

  // O usuário digitou/visualizou em UTC-3.
  // Para gravar em UTC, removemos o offset:
  // UTC = horário UTC-3 + 3 horas.
  const utcMs =
    Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      0
    ) -
    UTC_MINUS_3_OFFSET_MINUTES * 60 * 1000;

  return new Date(utcMs).toISOString();
}

// Inicializa o formulário com os dados do payment recebido via prop
const form = reactive({
  paymentId: props.payment.paymentId,
  subscriptionId: String(props.payment.subscriptionId ?? ""),
  mpPaymentId: props.payment.mpPaymentId ?? "",

  // ── PATCH: exibe createdAt com 3 horas a menos no frontend ──
  createdAt: toDatetimeLocalUtcMinus3(props.payment.createdAt),

  baseAmount: String(props.payment.baseAmount ?? ""),
  discountAmount: String(props.payment.discountAmount ?? "0"),
  finalAmount: String(props.payment.finalAmount ?? ""),
  couponId:
    props.payment.couponId != null ? String(props.payment.couponId) : "",
  couponCodeSnapshot: props.payment.couponCodeSnapshot ?? "",
  couponPercentSnapshot:
    props.payment.couponPercentSnapshot != null
      ? String(props.payment.couponPercentSnapshot)
      : "",
  planNameSnapshot: props.payment.planNameSnapshot ?? "",
  planPriceSnapshot:
    props.payment.planPriceSnapshot != null
      ? String(props.payment.planPriceSnapshot)
      : "",
  status: props.payment.status ?? "pending",
  statusDetail: props.payment.statusDetail ?? "",
  paymentMethod: props.payment.paymentMethod ?? "",
});

// Se a prop mudar (ex.: reabertura do modal com outro payment), resincroniza
watch(
  () => props.payment,
  (p) => {
    form.paymentId = p.paymentId;
    form.subscriptionId = String(p.subscriptionId ?? "");
    form.mpPaymentId = p.mpPaymentId ?? "";

    // ── PATCH: exibe createdAt com 3 horas a menos no frontend ──
    form.createdAt = toDatetimeLocalUtcMinus3(p.createdAt);

    form.baseAmount = String(p.baseAmount ?? "");
    form.discountAmount = String(p.discountAmount ?? "0");
    form.finalAmount = String(p.finalAmount ?? "");
    form.couponId = p.couponId != null ? String(p.couponId) : "";
    form.couponCodeSnapshot = p.couponCodeSnapshot ?? "";
    form.couponPercentSnapshot =
      p.couponPercentSnapshot != null ? String(p.couponPercentSnapshot) : "";
    form.planNameSnapshot = p.planNameSnapshot ?? "";
    form.planPriceSnapshot =
      p.planPriceSnapshot != null ? String(p.planPriceSnapshot) : "";
    form.status = p.status ?? "pending";
    form.statusDetail = p.statusDetail ?? "";
    form.paymentMethod = p.paymentMethod ?? "";
  }
);

async function handleSubmit() {
  errorMessage.value = "";

  if (!form.subscriptionId || isNaN(Number(form.subscriptionId))) {
    errorMessage.value = "ID da assinatura é obrigatório e deve ser numérico.";
    return;
  }
  if (form.baseAmount === "" || isNaN(Number(form.baseAmount))) {
    errorMessage.value = "Valor base é obrigatório e deve ser numérico.";
    return;
  }
  if (form.finalAmount === "" || isNaN(Number(form.finalAmount))) {
    errorMessage.value = "Valor final é obrigatório e deve ser numérico.";
    return;
  }
  if (!form.status.trim()) {
    errorMessage.value = "Status é obrigatório.";
    return;
  }

  // ── PATCH: createdAt obrigatório e enviado em UTC ───────────────
  const createdAtUtc = fromDatetimeLocalUtcMinus3ToUtc(form.createdAt);
  if (!createdAtUtc) {
    errorMessage.value = "Data de criação é obrigatória e deve ser válida.";
    return;
  }

  isLoading.value = true;
  try {
    await store.updatePayment({
      paymentId: form.paymentId,
      subscriptionId: Number(form.subscriptionId),
      mpPaymentId: form.mpPaymentId.trim() || null,

      // ── PATCH: grava em UTC no backend/base ─────────────────────
      createdAt: createdAtUtc,

      baseAmount: Number(form.baseAmount),
      discountAmount: Number(form.discountAmount) || 0,
      finalAmount: Number(form.finalAmount),
      couponId: form.couponId !== "" ? Number(form.couponId) : null,
      couponCodeSnapshot: form.couponCodeSnapshot.trim() || null,
      couponPercentSnapshot:
        form.couponPercentSnapshot !== ""
          ? Number(form.couponPercentSnapshot)
          : null,
      planNameSnapshot: form.planNameSnapshot.trim() || null,
      planPriceSnapshot:
        form.planPriceSnapshot !== "" ? Number(form.planPriceSnapshot) : null,
      status: form.status.trim(),
      statusDetail: form.statusDetail.trim() || null,
      paymentMethod: form.paymentMethod.trim() || null,
    });

    emit("refresh");
  } catch (error) {
    errorMessage.value =
      error.response?.data?.message ||
      error.message ||
      "Erro ao atualizar payment.";
  } finally {
    isLoading.value = false;
  }
}
</script>

<template>
  <div class="modal-backdrop" @click.self="emit('close')">
    <div ref="panelRef" class="modal-panel" :style="panelStyle">
      <!-- Cabeçalho -->
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h2 class="modal-title">Editar Pagamento #{{ form.paymentId }}</h2>
        <button
          class="modal-close-btn"
          :disabled="isLoading"
          @click="emit('close')"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0
                 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10
                 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293
                 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <!-- Corpo -->
      <div class="modal-body">
        <div v-if="errorMessage" class="form-error">{{ errorMessage }}</div>

        <div class="form-grid">
          <!-- Subscription ID -->
          <div class="form-field">
            <label class="form-label">ID Assinatura *</label>
            <input
              v-model="form.subscriptionId"
              type="number"
              min="1"
              class="form-input"
            />
          </div>

          <!-- MP Payment ID -->
          <div class="form-field">
            <label class="form-label">MP Payment ID</label>
            <input v-model="form.mpPaymentId" type="text" class="form-input" />
          </div>

          <!-- ── PATCH: CreatedAt editável, exibido em UTC-3 ── -->
          <div class="form-field">
            <label class="form-label">Data de Criação *</label>
            <input
              v-model="form.createdAt"
              type="datetime-local"
              class="form-input"
            />
          </div>

          <!-- Base Amount -->
          <div class="form-field">
            <label class="form-label">Valor Base *</label>
            <input
              v-model="form.baseAmount"
              type="number"
              min="0"
              step="0.01"
              class="form-input"
            />
          </div>

          <!-- Discount Amount -->
          <div class="form-field">
            <label class="form-label">Desconto</label>
            <input
              v-model="form.discountAmount"
              type="number"
              min="0"
              step="0.01"
              class="form-input"
            />
          </div>

          <!-- Final Amount -->
          <div class="form-field">
            <label class="form-label">Valor Final *</label>
            <input
              v-model="form.finalAmount"
              type="number"
              min="0"
              step="0.01"
              class="form-input"
            />
          </div>

          <!-- Status -->
          <div class="form-field">
            <label class="form-label">Status *</label>
            <select v-model="form.status" class="form-input form-select">
              <option value="none">—</option>
              <option value="pending">Pendente</option>
              <option value="approved">Aprovado</option>
              <option value="rejected">Rejeitado</option>
              <option value="cancelled">Cancelado</option>
              <option value="refunded">Estornado</option>
            </select>
          </div>

          <!-- Payment Method -->
          <div class="form-field">
            <label class="form-label">Método</label>
            <input
              v-model="form.paymentMethod"
              type="text"
              placeholder="pix, credit_card, courtesy…"
              class="form-input"
            />
          </div>

          <!-- Coupon ID -->
          <div class="form-field">
            <label class="form-label">ID Cupom</label>
            <input
              v-model="form.couponId"
              type="number"
              min="1"
              class="form-input"
            />
          </div>

          <!-- Coupon Code Snapshot -->
          <div class="form-field">
            <label class="form-label">Código Cupom</label>
            <input
              v-model="form.couponCodeSnapshot"
              type="text"
              class="form-input"
            />
          </div>

          <!-- Coupon Percent Snapshot -->
          <div class="form-field">
            <label class="form-label">% Desconto Cupom</label>
            <input
              v-model="form.couponPercentSnapshot"
              type="number"
              min="0"
              max="100"
              class="form-input"
            />
          </div>

          <!-- Plan Name Snapshot -->
          <div class="form-field">
            <label class="form-label">Plano (snapshot)</label>
            <input
              v-model="form.planNameSnapshot"
              type="text"
              class="form-input"
            />
          </div>

          <!-- Plan Price Snapshot -->
          <div class="form-field">
            <label class="form-label">Preço Plano (snapshot)</label>
            <input
              v-model="form.planPriceSnapshot"
              type="number"
              min="0"
              step="0.01"
              class="form-input"
            />
          </div>

          <!-- Status Detail (largura total) -->
          <div class="form-field form-field-full">
            <label class="form-label">Detalhe</label>
            <input
              v-model="form.statusDetail"
              type="text"
              maxlength="255"
              class="form-input"
            />
          </div>
        </div>
      </div>

      <!-- Rodapé -->
      <div class="modal-footer">
        <button
          class="btn btn-secondary"
          @click="emit('close')"
          :disabled="isLoading"
        >
          Cancelar
        </button>
        <button
          class="btn btn-primary"
          @click="handleSubmit"
          :disabled="isLoading"
        >
          <svg
            v-if="isLoading"
            class="w-5 h-5 mr-2 animate-spin"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
          <svg
            v-else
            class="w-5 h-5 mr-2"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"
            />
          </svg>
          <span>{{ isLoading ? "Salvando…" : "Salvar alterações" }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-panel {
  max-width: 680px;
  max-height: calc(100vh - 40px);
}

.modal-body {
  flex: 1;
}

.form-error {
  background: var(--danger-bg);
  border: 1px solid var(--danger);
  color: var(--danger-text);
  border-radius: 8px;
  padding: 0.6rem 0.9rem;
  font-size: 0.85rem;
  margin-bottom: 1rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem 1rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-field-full {
  grid-column: 1 / -1;
}

.form-input {
  height: 34px;
}

.form-select {
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 20 20' fill='%236b7280'%3E%3Cpath fill-rule='evenodd' d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z' clip-rule='evenodd'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
  background-size: 18px;
  padding-right: 1.8rem;
}

</style>